import React, { createContext, useContext, useMemo } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { LifeSpace, Project } from '@/src/renderer/lib/db/models'

/**
 * **Wo die Titelzeile steht** — für die Erkennung im Titel (#234). Der Bereich entscheidet bei
 * gleichnamigen Projekten, das Projekt der Seite meint ein `/Abschnitt` ohne `#`.
 */
export interface TitleRecognitionPlace {
  lifeSpace: LifeSpace
  projectId: string | null
  /** Bei gleichem Namen hinter `#` gewinnt dieses Projekt (Schnelleingabe: das im Hauptfenster offene). */
  preferredProjectId: string | null
  /** Alle Projekte, einmal je Seite gelesen — nicht je Zeile (eine Liste hat Hunderte). */
  projects: Project[]
}

const TitleRecognitionPlaceContext = createContext<TitleRecognitionPlace | null>(null)

export interface TitleRecognitionScopeProps {
  lifeSpace: LifeSpace
  /** Das Projekt der Seite; `null` in Kurz notiert, Demnächst, Wiederkehrend und der Schnelleingabe. */
  projectId?: string | null
  /** Nur Vorzug bei gleichem Namen, kein Seitenprojekt — für die Schnelleingabe. */
  preferredProjectId?: string | null
  children: React.ReactNode
}

/**
 * **Schaltet die Erkennung im Titel ein** — die Seite legt ihn um ihre Listen.
 *
 * Ein Kontext statt einer Prop, weil die Titelzeile tief in Liste, Tafel und Tagesgruppe steckt. **Ohne
 * ihn keine Erkennung**: So bleiben Stellen still, deren Anlegen mit einem Ziel aus dem Titel nichts
 * anfangen könnte (Stories, Ruhend, Geschafft).
 *
 * @example
 * <TitleRecognitionScope lifeSpace={lifeSpace} projectId={projectId}>
 *   <TaskList … />
 * </TitleRecognitionScope>
 */
export const TitleRecognitionScope: React.FC<TitleRecognitionScopeProps> = ({ lifeSpace, projectId = null, preferredProjectId = null, children }) => {
  const projects = useDataSource().useProjects()
  const value = useMemo(
    () => ({ lifeSpace, projectId, preferredProjectId, projects: projects ?? [] }),
    [lifeSpace, projectId, preferredProjectId, projects],
  )
  return <TitleRecognitionPlaceContext.Provider value={value}>{children}</TitleRecognitionPlaceContext.Provider>
}

export function useTitleRecognitionPlace(): TitleRecognitionPlace | null {
  return useContext(TitleRecognitionPlaceContext)
}
